import { useMemo, useState, type ReactNode } from 'react'
import { motion } from 'motion/react'
import { Check, Plus, Search } from 'lucide-react'
import { COURSES, COURSE_LABELS, normaliseName, type Course } from '@shared/events'
import { PLATE_BUCKET_LABELS, recipesById, type PlateBucket } from '@shared/plate'
import { useEvents } from '../lib/hooks'
import { COLOR_PORCION, porcionesDeReceta } from '../lib/platos'
import { Hoja } from './Hoja'

export interface Eleccion {
  recipeId: string | null
  freeText: string
}

const PORCIONES: PlateBucket[] = ['vegetal', 'proteina', 'cereal']

/**
 * Elegir qué toca: una receta del recetario o algo escrito a mano.
 *
 * El texto libre no es un caso raro: "sobras", "cena fuera" y "pizza" son
 * planes legítimos, y obligar a crear una receta para cada uno haría que la
 * gente dejara de planificar.
 *
 * Con `cursos`, la hoja deja escoger también qué plato de la comida es:
 * primero, segundo, guarnición o postre.
 */
export function ElegirReceta({
  titulo,
  descripcion = 'Toca una receta para ponerla, o escribe otra cosa.',
  actual,
  cursos,
  cursoInicial,
  onElegir,
  onCerrar,
  extra,
}: {
  titulo: string
  descripcion?: string
  /** La receta que ya está puesta, para marcarla. */
  actual?: string | null
  /** Los platos de la comida que se pueden escoger; sin ellos, no se pregunta. */
  cursos?: readonly Course[]
  cursoInicial?: Course
  onElegir: (eleccion: Eleccion, curso: Course | null) => void
  onCerrar: () => void
  extra?: ReactNode
}) {
  const events = useEvents()
  const [busqueda, setBusqueda] = useState('')
  const [curso, setCurso] = useState<Course | null>(cursoInicial ?? cursos?.[0] ?? null)

  const recetas = useMemo(() => {
    const needle = normaliseName(busqueda)
    return [...recipesById(events)]
      .map(([id, recipe]) => ({ id, name: recipe.name, porciones: porcionesDeReceta(recipe) }))
      .filter((recipe) => !needle || normaliseName(recipe.name).includes(needle))
      .sort((a, b) => a.name.localeCompare(b.name, 'es'))
  }, [events, busqueda])

  const texto = busqueda.trim()
  const elegir = (eleccion: Eleccion): void => onElegir(eleccion, curso)

  return (
    <Hoja titulo={titulo} descripcion={descripcion} onCerrar={onCerrar}>
      {extra}

      {cursos && cursos.length > 0 && (
        <div className="-mx-5 mb-3 flex gap-2 overflow-x-auto px-5 pb-1" role="radiogroup" aria-label="Qué plato es">
          {COURSES.filter((value) => cursos.includes(value)).map((value) => (
            <Chip key={value} activo={curso === value} onClick={() => setCurso(value)}>
              {COURSE_LABELS[value]}
            </Chip>
          ))}
        </div>
      )}

      <div className="relative">
        <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          type="search"
          className="campo pl-10"
          value={busqueda}
          placeholder="Buscar receta o escribir otra cosa"
          aria-label="Buscar receta o escribir otra cosa"
          onChange={(event) => setBusqueda(event.target.value)}
          autoFocus
        />
      </div>

      <div className="mt-3 space-y-1.5 pb-2">
        {texto && !recetas.some((recipe) => normaliseName(recipe.name) === normaliseName(texto)) && (
          <button
            type="button"
            onClick={() => elegir({ recipeId: null, freeText: texto })}
            className="flex w-full items-center gap-2 rounded-2xl border border-dashed border-primary/50 px-3.5 py-3 text-left text-sm font-medium text-primary outline-none transition-colors hover:bg-accent/70 focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Plus className="h-4 w-4 shrink-0" strokeWidth={2.4} />
            Poner «{texto}» sin receta
          </button>
        )}

        {recetas.length === 0 && !texto && (
          <p className="px-1 py-6 text-center text-sm text-muted-foreground">
            No hay recetas todavía. Escribe lo que vayáis a comer.
          </p>
        )}

        {recetas.map((recipe, index) => {
          const enUso = recipe.id === actual
          return (
            <motion.button
              key={recipe.id}
              type="button"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.18, delay: Math.min(index, 8) * 0.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => elegir({ recipeId: recipe.id, freeText: '' })}
              className={`flex min-h-12 w-full items-center gap-3 rounded-2xl border px-3.5 py-2.5 text-left outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring ${
                enUso ? 'border-sage/50 bg-filled' : 'border-transparent hover:bg-accent/70'
              }`}
            >
              <span className="min-w-0 flex-1 truncate text-sm font-medium">{recipe.name}</span>
              <span className="flex shrink-0 items-center gap-1" aria-hidden="true">
                {PORCIONES.map((bucket) => (
                  <span
                    key={bucket}
                    title={PLATE_BUCKET_LABELS[bucket]}
                    className={`h-1.5 w-1.5 rounded-full ${
                      recipe.porciones.has(bucket) ? COLOR_PORCION[bucket] : 'bg-surface'
                    }`}
                  />
                ))}
                {enUso && <Check className="ml-1 h-4 w-4 text-sage-deep" />}
              </span>
            </motion.button>
          )
        })}
      </div>
    </Hoja>
  )
}

export function Chip({
  activo,
  onClick,
  children,
}: {
  activo: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={activo}
      onClick={onClick}
      className={`label-caps shrink-0 rounded-full border px-3.5 py-2.5 outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring ${
        activo
          ? 'border-primary bg-primary text-primary-foreground'
          : 'border-border bg-background text-muted-foreground hover:border-primary/40'
      }`}
    >
      {children}
    </button>
  )
}
